import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { ComponentBase, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { AuthService } from 'app/um/services/auth-service';
import { Toaster } from 'app/shared/lib/toaster';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import { deepClone } from 'app/shared/lib/utilities';
import { ProviderService } from 'app/cd/services/provider-service';
import { EssRuleElement, validate } from 'app/shared/elements/ess-rule';
import { apiCall } from 'app/shared/lib/api/api';
import { getReference } from 'app/shared/models/essette-types';
import { fireEvents } from 'app/shared/lib/fire-events';
import { IAuthCreatedEventDetail } from 'app/um/lib/events';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MemberService, MemberType } from 'app/cd/services/member-service';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { IBaseConfig } from 'app/core/models/essette-interfaces';
import { guid } from 'app/shared/lib/guid';
import { NamespaceOptions } from 'app/shared/lib/i18n';

@autoinject()
export class CreateAuthStepCustomElement extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.boolean()
    public readonly: boolean = false;
    @bindable.object({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'reload' })
    protected auth: UM.Auth = {};
    @bindable.stringNull()
    public memberPlanId: string | null = null;

    protected authClasses: UM.AuthClass[] = [];
    protected authSubClasses: UM.AuthSubClass[] = [];
    protected authTypes: UM.AuthType[] = [];
    protected priorities: UM.AuthPriority[] = [];
    protected locations: CD.ProviderLocationUserDetailModel[] = [];
    protected selectedLocation: CD.ProviderLocationUserDetailModel | null = null;
    protected memberPlan: CD.MemberPlan | null = null;
    protected memberType: MemberType = MemberType.Patient;

    @observable({ changeHandler: 'selectedClassChanged' })
    protected selectedClass: UM.AuthClass | null = null;
    protected selectedSubClass: UM.AuthSubClass | null = null;

    protected validateForm = validate;
    protected $isValid!: boolean;

    protected availableFilters = ['identifier.primary', 'identifier.medicaid', 'name.first', 'name.last', 'birthDate', 'primaryCarePhysician', 'primaryCarePhysician.name.first', 'primaryCarePhysician.name.last', 'healthPlan', 'lineOfBusiness'];
    protected requiredFilters: string | null = null;

    protected memberPlanConfig: IBaseConfig | null = null;

    protected i18nNamespace: NamespaceOptions = NamespaceOptions.PP;

    protected id: guid = guid();

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly authService: AuthService,
        private readonly providerService: ProviderService,
        private readonly memberService: MemberService,
        private readonly toaster: Toaster,
        private $element: Element,
        ...rest: any[]) {

        super(...rest);

        this.logger.source = this;
        this.memberPlanConfig = { serviceRootUrl: 'pp/members/plans/available' };
        this.memberService.configure(this.memberPlanConfig);
    }

    //protected bind(bindingContext: Object, overrideContext: Object): void {    }


    ////****************** Events ***************************************************////
    protected selectedClassChanged(newAuthClass: UM.AuthClass | null, oldAuthClass: UM.AuthClass | null): void {
        if(newAuthClass && oldAuthClass && newAuthClass.id !== oldAuthClass.id) {
            this.selectedSubClass = null;
            this.auth.type = null;
        }
    }

    protected dateOfServiceChanged(dateOfService: string): void {
        if(!dateOfService) { return; }
        if(this.auth.type) { return; }

        if(this.env.today === dateOfService) {
            this.auth.type = this.authTypes.find(t => t.code === 'CONC') || null;
        }

        if(this.env.today > dateOfService) {
            this.auth.type = this.authTypes.find(t => t.code === 'POST') || null;
        }

        if(this.env.today < dateOfService) {
            this.auth.type = this.authTypes.find(t => t.code === 'PRE') || null;
        }
    }


    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        //JW Move this up, to clear data and then load the member plan, then if we have an auth we can finish the details after loading the lookups
        if(!this.auth?.id) {
            this.reset();
            if(this.memberPlanId) {
                this.loadMemberPlan(this.memberPlanId);
            }
        }

        await Promise.all([
            this.loadLookups(),
            this.loadMemberPlanSettings()
        ]);

        if(this.auth?.id) {
            //check to see if locations in provider.requesting match available locations
            if(this.locations.find(l => l.id === this.auth?.provider?.requesting?.location?.id) === null) {
                this.auth = {};
                throw new Error("Auth requesting provider does not match available locations.");
            }
            this.selectedClassChanged(this.auth?.class || null, null);

            this.memberPlan = this.auth?.member?.plan || null;
            if(this.memberPlan) this.memberPlan.member = deepClone(this.auth?.member!);
            if(this.auth?.submittedBy) {
                this.selectedLocation = this.auth?.submittedBy || null;
            }

            if(this.auth?.class) this.selectedClass = deepClone(this.auth?.class);
            if(this.auth?.class?.subClass) this.selectedSubClass = deepClone(this.auth?.class?.subClass);
        }
    }

    private async loadLookups(): Promise<void> {
        const options: StandardLoadOptions = {
            all: true
        };

        const locationIncludes: string[] = [
            'providerLocations',
            'providerLocations.facility'
        ];

        try {
            this.state.beginLoad();
            const [classes, types, userContexts] = await Promise.all([
                this.authService.loadAuthClasses(options, ['subClasses']),
                this.authService.loadAuthTypes(true, options),  //load all auth types, even IP only ones
                this.providerService.loadProviderUserContexts(options, locationIncludes)
            ]);

            this.authClasses = classes.results;
            this.authTypes = types.results;
            const context = userContexts.results.find((uc) => uc.user?.id === this.env.user.id);
            this.locations = context?.providerLocations || [];

        } catch(error) {
            this.logger.error('Error loading lookups', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    protected reset() {
        this.memberPlan = null;
        this.selectedLocation = null;
        this.selectedClass = null;
        this.selectedSubClass = null;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async createAuth(): Promise<void> {

        const newAuth: UM.Auth = {
            class: getReference<UM.AuthClassWithSubClassModel>(this.selectedClass, {
                subClass: getReference(this.selectedSubClass)
            }),
            member: {
                plan: getReference(this.memberPlan)
            },
            type: getReference(this.auth?.type),
            submittedBy: getReference(this.selectedLocation),
            requestedBy: getReference(this.env.user),
            assignedTo: getReference(this.auth?.assignedTo),
            serviceDate: this.auth?.serviceDate,
            provider: {
                requesting: {
                    location: getReference(this.selectedLocation)
                }
            },
            admission: { date: this.selectedSubClass?.isAdmission && !this.auth.type?.flag?.usesPreserviceDay ? this.auth.serviceDate?.dateRange?.start : null }
        };

        try {
            this.state.beginSave();

            const response = await this.authService.insertAuth(newAuth);

            if(response.isSuccessful) {
                this.toaster.standard.saved("um:auth.$", true);
                if(this.$element && response.id) {
                    const detail: IAuthCreatedEventDetail = { authId: response.id };

                    /** Initialize action includes adding any assessments required by the auth's sub-class (if they don't exist) */
                    await this.initializeAuth(response.id);

                    fireEvents(this.$element, ['auth-created'], detail);
                }
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async initializeAuth(authId: string): Promise<boolean> {
        try {
            this.state.beginSave();
            const response = await this.authService.initializeAuth(authId);
            return response.isSuccessful;
        } catch(error) {
            this.logger.error('Error calling initialize action on auth');
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    protected async loadMemberPlan(memberPlanId: string): Promise<void> {
        try {
            this.state.beginSave();

            const response = await this.memberService.loadMemberPlan(memberPlanId, ['member']);
            this.memberPlan = response.results[0];

        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    protected async loadMemberPlanSettings(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.memberService.loadMemberPlanSettings()
            const settings = response.settings;

            this.requiredFilters = (getApiSetting(settings, 'requiredFilters.$required') as string);
            const availableFilters = (getApiSetting(settings, 'availableFilters.$available') as string);

            if(availableFilters) {
                this.availableFilters = availableFilters.split(' ');
            }

        } catch(error) {
            this.logger.error('Error loading lookups', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
